import { messages } from '@/lib/messages'

/** RFC 9457 `application/problem+json` — sözleşme §2. */
export interface ProblemFieldError {
  field: string
  message: string
}

/** `row_errors` (400): toplu kaydette reddedilen satır; `row` istekteki 0 tabanlı sıra (sözleşme §2.2). */
export interface ProblemRowError {
  row: number
  field: string | null
  message: string
}

export interface Problem {
  type: string
  title: string
  status: number
  code: string
  detail?: string | null
  instance?: string | null
  errors?: ProblemFieldError[]
  /** `record_changed` (409): kaydın sunucudaki güncel sürümü, değiştiren ve zamanı (docs/CRUD-ALTYAPI-PLAN.md Faz 1). */
  version?: number
  changed_by?: string | null
  changed_at?: string | null
  /** `row_errors` (400): satır hataları; `record_changed` toplu kaydette bayat satırın sırası `row`. */
  rows?: ProblemRowError[]
  row?: number
}

/** Tüm HTTP hataları bu sınıfla fırlatılır; arayüz yalnızca bunu tanır. */
export class ApiError extends Error {
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
    this.problem = problem
  }

  get status(): number {
    return this.problem.status
  }

  get code(): string {
    return this.problem.code
  }

  get fieldErrors(): ProblemFieldError[] {
    return this.problem.errors ?? []
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function parseProblem(payload: unknown, status: number): Problem {
  if (isRecord(payload) && typeof payload.title === 'string') {
    return {
      type: typeof payload.type === 'string' ? payload.type : 'about:blank',
      title: payload.title,
      status: typeof payload.status === 'number' ? payload.status : status,
      code: typeof payload.code === 'string' ? payload.code : fallbackCode(status),
      detail: typeof payload.detail === 'string' ? payload.detail : null,
      instance: typeof payload.instance === 'string' ? payload.instance : null,
      errors: Array.isArray(payload.errors)
        ? payload.errors.filter(isFieldError).map((item) => ({
            field: item.field,
            message: item.message,
          }))
        : undefined,
      ...(typeof payload.version === 'number' ? { version: payload.version } : {}),
      ...(typeof payload.changed_by === 'string' || payload.changed_by === null
        ? { changed_by: payload.changed_by }
        : {}),
      ...(typeof payload.changed_at === 'string' || payload.changed_at === null
        ? { changed_at: payload.changed_at }
        : {}),
      ...(Array.isArray(payload.rows) ? { rows: payload.rows.filter(isRowError) } : {}),
      ...(typeof payload.row === 'number' ? { row: payload.row } : {}),
    }
  }
  return {
    type: 'about:blank',
    title: fallbackTitle(status),
    status,
    code: fallbackCode(status),
    detail: null,
  }
}

function isFieldError(value: unknown): value is ProblemFieldError {
  return isRecord(value) && typeof value.field === 'string' && typeof value.message === 'string'
}

function isRowError(value: unknown): value is ProblemRowError {
  return (
    isRecord(value) &&
    typeof value.row === 'number' &&
    (typeof value.field === 'string' || value.field === null) &&
    typeof value.message === 'string'
  )
}

function fallbackCode(status: number): string {
  switch (status) {
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 422:
      return 'invalid_input'
    case 502:
      return 'upstream_error'
    default:
      return 'error'
  }
}

function fallbackTitle(status: number): string {
  switch (status) {
    case 401:
      return messages.errors.unauthorized
    case 403:
      return messages.errors.forbidden
    case 404:
      return messages.errors.notFound
    case 409:
      return messages.errors.conflict
    case 422:
      return messages.errors.validation
    case 502:
      return messages.errors.upstream
    default:
      return messages.errors.unexpected
  }
}

/** Ağ kesintisi gibi HTTP yanıtı olmayan durumlar için sentetik problem. */
export function networkProblem(detail?: string): Problem {
  return {
    type: 'about:blank',
    title: messages.errors.network,
    status: 0,
    code: 'network_error',
    detail: detail ?? null,
  }
}

/**
 * API dışı hatalar (ör. Cube istemcisinin `RequestError`'ı) ham mesajı arayüze taşımaz: mesaj kaynak
 * veritabanının iç sunucu adını içerebilir. Ayrıntı yalnız konsola yazılır, kullanıcı genel metni görür.
 */
export function toProblem(error: unknown): Problem {
  if (error instanceof ApiError) return error.problem
  if (error instanceof Error) {
    console.warn('İstek hatası', error)
    const status = 'status' in error && typeof error.status === 'number' ? error.status : 0
    return {
      type: 'about:blank',
      title: status >= 500 ? messages.errors.upstream : messages.errors.unexpected,
      status,
      code: status >= 500 ? 'upstream_error' : 'error',
      detail: null,
    }
  }
  return networkProblem()
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401
}
